import axios from "@axios";
import moment from "moment";

import { puestosHipicosAmount } from "@/views/pages/reports/puestos-hipicos/puestosHipicos";
import {
  betbyIsSettled,
  betbyStatusKey,
} from "@/views/pages/reports/ticket-sports-live/betby/betbyStatus";
import { HORSES_V2_ENDPOINTS } from "@/views/pages/reports/horses-v2/horsesV2";
import {
  ROJO_NEGRO_ENDPOINTS,
  rojoNegroSelectionBadges,
} from "@/views/pages/reports/rojo-negro/rojoNegro";
import {
  DIGITAIN_BET_TYPE_LABELS,
  DIGITAIN_ENDPOINTS,
  DIGITAIN_STATUS_LABELS,
  digitainIsSettled,
  digitainSelectionStatus,
} from "@/views/pages/reports/digitain/digitain";
import { formatWlDateTime, formatWlWallTime } from "@/shared/utils/whitelabelDate";
import { formatOdds } from "@/shared/utils/odds";

/**
 * Resolución de "transacción de billetera -> ticket del proveedor".
 *
 * El historial de billetera guarda, para cada jugada, dos identificadores que
 * vienen del gateway (ver whish-gateway-api):
 *
 *   reference     -> id del ticket/apuesta del proveedor (id_bet, bet_code,
 *                    Ticket.Code, transaction_id, bet_id según el producto)
 *   transactionID -> el mismo id prefijado ("BET-", "WIN-", "RB-", ...)
 *
 * La mayoría de los productos ya están cubiertos por el endpoint unificado
 * POST /report/transaction-details (sportsbook, sportsw1, sportsw2, betby,
 * centerhorses). Novubet, BetPrime, Puestos Hípicos, Tablas, Rojo y Negro y
 * Digitain quedaron fuera de ese endpoint y siguen teniendo el suyo propio.
 *
 * Cada resolver normaliza al mismo contrato:
 *
 *   {
 *     summary: [{ key, value, labelKey?, badges? }],
 *     selections: [ ...filas... ],
 *     selectionFields?: [{ key, labelKey, translate? }],
 *     selectionsTitleKey?: string,
 *     warningKey?: string,
 *     html?: string,
 *   }
 *
 * `labelKey` y `selectionFields` son para los productos cuyas columnas no son
 * las de un ticket deportivo (mercado / selección / evento / liga / deporte):
 * si no vienen, el modal usa las etiquetas de wallet_ticket.* por defecto.
 *
 * Si un producto no está mapeado el modal degrada solo: muestra el aviso y
 * deja el JSON crudo disponible en "ver detalle técnico".
 */

// Operaciones que pueden tener un ticket detrás. El resto (DEBIT, CREDIT,
// AJUST_CRED, AJUST_DEBIT) son recargas, retiros, bonos y ajustes manuales:
// no hay apuesta que mostrar.
export const TICKET_OPERATIONS = ["BET", "WIN", "ROLLBACK"];

// productId (tal como lo escribe el gateway en la transacción) -> resolver.
export const PRODUCT_FAMILY = {
  sportsbook: "unified", // Deportes (pre-match) - SportsBookPlay
  sportsw1: "unified", // Deportes en vivo - SW3 / Sportsw
  sportsw2: "unified", // Deportes en vivo - SW3 / Sportsw v2
  betby: "unified", // Deportes en vivo - BetBy
  centerhorses: "unified", // Caballos
  centerhorsesv2: "centerhorsesv2", // Caballos (v2)
  newsport: "novubet", // Deportes en vivo - Novubet / NewSport
  betprime: "betprime", // Deportes en vivo - BetCore / BetPrime
  puestoshipicos: "puestoshipicos", // Hipismo por puestos - Puestos Hípicos
  tablas: "tablas",
  rojonegro: "rojonegro",
  digitain: "digitain",
};

export function familyFor(transaction) {
  return PRODUCT_FAMILY[transaction?.productId] || null;
}

export function hasTicketOperation(transaction) {
  return TICKET_OPERATIONS.includes(transaction?.operation);
}

/**
 * Cada producto escribe el estado del ticket a su manera: SportsBookPlay en
 * MAYUSCULAS en ingles (WON, NULL_BET), CenterHorses en español ya traducido
 * ("Ganado", "En juego"), SW3 en femenino ("Ganada", "perdedora") y BetPrime
 * en minúscula ("win", "bet_cancel"). Los unificamos contra las claves que ya
 * usa el i18n (status.*) para poder pintar un badge coherente.
 *
 * Los valores salen de las tablas de Reportes -> Tickets: sports/betsPlaced.vue,
 * horses/betsPlaced.vue, ticket-sports-live/detailSw3.vue y liveBetting/detail.vue.
 */
const STATUS_ALIASES = {
  // pendiente / apostada
  pending: "pending",
  pendiente: "pending",
  bet: "pending",
  apuesta: "pending",
  waiting: "pending",
  // ganada
  won: "won",
  win: "won",
  ganado: "won",
  ganada: "won",
  ganadora: "won",
  // perdida
  lost: "lost",
  lose: "lost",
  perdido: "lost",
  perdida: "lost",
  perdedora: "lost",
  // pagada
  paid: "paid",
  pagado: "paid",
  pagada: "paid",
  // anulada / cancelada
  null_bet: "null_bet",
  anulado: "null_bet",
  anulada: "null_bet",
  cancel: "null_bet",
  cancelado: "null_bet",
  cancelada: "null_bet",
  canceled: "null_bet",
  cancelled: "null_bet",
  bet_cancel: "null_bet",
  // vencida
  expired: "expired",
  vencido: "expired",
  vencida: "expired",
  // devolución
  return: "return",
  devolucion: "return",
  refund: "return",
  // en juego
  ingame: "ingame",
  "en juego": "ingame",
  // otros
  cashout: "cashout",
  retirado: "cashout",
  rollback: "rollback",
  settlement: "settlement",
  suspended: "suspended",
};

// Variantes de bootstrap en vez de hex fijos, para que el badge siga el tema
// del admin (claro/oscuro) como ya hace novubet/detail.vue.
const STATUS_VARIANTS = {
  won: "success",
  paid: "success",
  settlement: "success",
  lost: "danger",
  pending: "info",
  ingame: "warning",
  suspended: "warning",
};

/**
 * Devuelve { i18nKey, raw, variantKey }:
 *
 *   i18nKey   -> clave completa de traducción, o null si el estado no está
 *                mapeado; en ese caso el componente muestra el valor crudo del
 *                proveedor en vez de inventar una traducción.
 *   variantKey-> clave con la que STATUS_VARIANTS elige el color del badge.
 *
 * Acepta el estado como string (el caso normal: se busca en STATUS_ALIASES) o
 * como objeto `{ i18nKey, raw, variantKey }` cuando el resolver ya sabe dónde
 * vive la traducción. Lo segundo hace falta para Puestos Hípicos, cuyos estados
 * ("ganó la mitad", "cortada", "eliminada"...) viven en
 * puestos_hipicos.provider_status.* y no en status.*.
 */
export function normalizeStatus(raw) {
  if (raw === null || raw === undefined || raw === "") return null;

  if (typeof raw === "object") {
    const value = raw.raw === null || raw.raw === undefined ? "" : String(raw.raw);
    if (!raw.i18nKey && !value) return null;
    return {
      i18nKey: raw.i18nKey || null,
      raw: value,
      variantKey: raw.variantKey || null,
    };
  }

  const value = String(raw).trim();
  const key = STATUS_ALIASES[value.toLowerCase()] || null;
  return { i18nKey: key ? `status.${key}` : null, raw: value, variantKey: key };
}

export function statusVariant(key) {
  return STATUS_VARIANTS[key] || "secondary";
}

const fmtDate = (value) => formatWlWallTime(value);

const fmtDateUtc = (value) => formatWlDateTime(value);

/**
 * Importes del resumen del ticket, siempre con dos decimales.
 *
 * Cada proveedor manda el monto a su manera: entero (65), string ("296.63"),
 * un objeto { CURRENCY } o el producto de monto por cuota, que arrastra el
 * error del punto flotante (65 * 1.2 -> 77.99999999999999). El modal los
 * pintaba crudos, así que un premio de 29.663,00 se leía "29663" y otro salía
 * con una ristra de decimales. Puestos Hípicos no pasa por acá: ya trae sus
 * montos formateados con puestosHipicosAmount.
 */
const money = (value) => {
  if (value === null || value === undefined || value === "") return "-";

  const parsed = Number(value);
  if (!Number.isFinite(parsed)) {
    return typeof value === "object" ? "-" : String(value);
  }

  return parsed.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const fmtUnix = (value) =>
  value ? formatWlDateTime(moment.unix(Number(value)).toDate()) : "-";

const vs = (a, b) => [a, b].filter(Boolean).join(" vs ");

const CLOSING_KEYS = {
  cashout: "cashout_at",
  settlement: "settled_at",
  refund: "refunded_at",
};

const byCreatedAt = (a, b) => new Date(a?.createdAt) - new Date(b?.createdAt);

const closingRow = (kind, createdAt) =>
  CLOSING_KEYS[kind] && createdAt
    ? { key: CLOSING_KEYS[kind], value: fmtDate(createdAt) }
    : null;

const optionalRow = (row) => (row ? [row] : []);

/** El id "pelado" del ticket: lo que el proveedor conoce como número de apuesta. */
export function ticketReference(transaction) {
  const reference = transaction?.reference;
  if (reference) return String(reference);
  // Fallback: algunas transacciones viejas solo traen transactionID prefijado.
  return String(transaction?.transactionID || "").replace(
    /^(BET-|WIN-|RB-|RO-|CO-|DIF-|INS-|RBWIN-)/,
    ""
  );
}

/** El id prefijado, que es con lo que BetBy indexa sus transacciones. */
function prefixedReference(transaction) {
  if (transaction?.transactionID) return String(transaction.transactionID);
  const reference = ticketReference(transaction);
  return reference ? `BET-${reference}` : "";
}

/* ------------------------------------------------------------------ */
/* Endpoint unificado: sportsbook, sportsw1/2, betby, centerhorses     */
/* ------------------------------------------------------------------ */

/** Normaliza la respuesta de SportsBookPlay (pre-match). */
function normalizeSportsbook(data) {
  const ticket = data?.SportsBookPlay?.response || data?.response || data;
  if (!ticket || !ticket.bet) return null;

  // selections viene como un array de mapas ({ "0": {...}, "1": {...} }).
  const selections = [];
  (ticket.selections || []).forEach((group) => {
    Object.keys(group || {}).forEach((key) => selections.push(group[key]));
  });

  return {
    summary: [
      { key: "ticket_number", value: ticket.bet },
      { key: "status", value: ticket.status },
      {
        key: "bet_amount",
        value: money(ticket.amount?.CURRENCY ?? ticket.amount),
      },
      {
        key: "potential_win",
        value: money(
          ticket.final_bonus_amount?.CURRENCY ?? ticket.final_bonus_amount,
        ),
      },
      { key: "created", value: fmtDateUtc(ticket.created) },
    ],
    selections: selections.map((s) => ({
      market: s?.outcome?.name,
      selection: s?.select?.text,
      event: s?.event?.name,
      league: s?.event?.league?.name,
      sport: s?.event?.sport?.name,
      odds: formatOdds(s?.priceDecimalPercentage, s?.priceAmerican),
      start: fmtDateUtc(s?.event?.start_date || s?.event?.startDate),
      // SportsBookPlay marca cada pata del parlay por separado: es lo que le
      // permite al operador ver cuál seleccion tumbó el ticket.
      status: s?.status,
    })),
  };
}

/** Normaliza la respuesta de SW3 / Sportsw (v1 y v2). */
function normalizeSw(data) {
  const rows = Array.isArray(data) ? data : data?.response ?? [];
  const ticket = rows.find((r) => r.bets && r.bets.length) || rows[0];
  if (!ticket) return null;

  return {
    summary: [
      { key: "ticket_number", value: ticket.referenceId },
      { key: "status", value: ticket.ticketStatus },
      { key: "bet_amount", value: money(ticket.amount ?? ticket.debit_amount) },
      {
        key: "potential_win",
        value: money(ticket.to_win ?? ticket.credit_amount),
      },
      { key: "created", value: fmtDate(ticket.date) },
    ],
    selections: (ticket.bets || []).map((bet) => {
      const extra = bet.extra_data || bet.extraData || {};
      return {
        market: bet.referencia ?? bet.marketReference,
        selection: extra.apuesta ?? bet.selection,
        event: vs(bet.equipoa ?? bet.home, bet.equipob ?? bet.away),
        league: extra.liga ?? extra.competitionName,
        sport: extra.deporte ?? bet.sport,
        odds: formatOdds(bet.seleccion_ref?.price ?? bet.price),
        start: extra.text_info ?? extra.textInfo ?? "-",
      };
    }),
  };
}

const BETBY_STATUS_VARIANTS = {
  pending: "pending",
  win: "won",
  lost: "lost",
};

/**
 * Tipo de apuesta de un betslip de BetBy: simple, combinada o BetBuilder.
 *
 * Un BetBuilder combina varios mercados del MISMO evento ("1st half -
 * handicap" + "1st half - total" del mismo partido), que es justamente lo que
 * una combinada normal no permite por estar correlacionados. Esa es la señal
 * que se usa para reconocerlo.
 *
 * Si BetBy empieza a mandar una bandera propia (`bet_builder`,
 * `is_bet_builder`, `bet_type`) se prefiere esa: la estructura queda de
 * respaldo. Ojo si se cambia: pasar de la bandera a la heurística o al revés
 * cambia lo que ve el operador en pantalla.
 */
export function betbyBetType(betslip = {}, bets = []) {
  const explicita =
    betslip.bet_builder ??
    betslip.is_bet_builder ??
    betslip.betBuilder ??
    null;

  if (explicita === true) return "wallet_ticket.bet_type_builder";
  if (typeof betslip.bet_type === "string" &&
      /builder/i.test(betslip.bet_type)) {
    return "wallet_ticket.bet_type_builder";
  }

  if (!Array.isArray(bets) || bets.length === 0) return null;
  if (bets.length === 1) return "wallet_ticket.bet_type_single";

  // Identificamos el evento por su id si viene, y si no por el partido más su
  // horario: dos equipos pueden repetirse en fechas distintas.
  const eventos = new Set(
    bets.map((bet) => {
      const id = bet?.event_id ?? bet?.match_id ?? bet?.eventId;
      if (id !== undefined && id !== null && id !== "") return `id:${id}`;

      const competidores = Array.isArray(bet?.competitor_name)
        ? bet.competitor_name.join(" vs ")
        : bet?.competitor_name ?? "";
      return `ev:${competidores}|${bet?.scheduled ?? ""}`;
    })
  );

  return eventos.size === 1
    ? "wallet_ticket.bet_type_builder"
    : "wallet_ticket.bet_type_combo";
}

/**
 * BetBy maneja los importes en unidades menores: una apuesta de 3.485 VES
 * viaja como 348500. El gateway ya divide por 100 el monto de cada
 * transacción al recibirla (BetByController: `transaction.amount / 100`, sin
 * mirar la moneda) y vuelve a multiplicar al informar el saldo, así que el
 * factor fijo es la convención de toda la integración.
 *
 * Lo que quedaba sin convertir era `potential_win`, que se lee crudo del
 * payload guardado: el premio salía 100 veces más grande —1.110.321,00 donde
 * el ticket paga 11.103,21—. El monto apostado NO pasa por acá: el gateway ya
 * lo dividió.
 */
const BETBY_UNIDADES_MENORES = 100;

const betbyAmount = (value) => {
  // Sin valor se devuelve tal cual: Number(null) es 0, y un premio ausente
  // tiene que seguir mostrándose como "-" y no como 0,00.
  if (value === null || value === undefined || value === "") return value;

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed / BETBY_UNIDADES_MENORES : value;
};

const BETBY_CLOSING_KINDS = {
  win: "settlement",
  lost: "settlement",
  cashout: "cashout",
  refund: "refund",
  discard: "refund",
};

export function betbyClosing(data) {
  const [last] = [...(data?.relatedTransactions || [])].sort(byCreatedAt).reverse();
  return closingRow(BETBY_CLOSING_KINDS[data?.ticketStatus], last?.createdAt);
}

const betbyStatus = (status) => {
  if (!status) return "";
  return {
    i18nKey: betbyStatusKey(status),
    raw: status,
    variantKey: BETBY_STATUS_VARIANTS[status] || null,
  };
};

function betbyPayoutRow(data, request, betslip) {
  const paid = data?.paidAmount;
  if (betbyIsSettled(data?.ticketStatus) && paid !== null && paid !== undefined) {
    return { key: "win_amount", value: money(paid) };
  }

  return {
    key: "potential_win",
    value: money(
      betbyAmount(
        request.potential_win ??
          request.transaction?.potential_win ??
          betslip.potential_win,
      ),
    ),
  };
}

/** Normaliza la respuesta de BetBy. */
function normalizeBetby(data, transaction) {
  const request = data?.transaction?.details?.request || data?.request || {};
  const betslip = request.betslip || request.transaction?.betslip || {};
  const bets = betslip.bets || [];
  if (!bets.length) return null;

  const betType = betbyBetType(betslip, bets);

  return {
    summary: [
      { key: "ticket_number", value: ticketReference(transaction) },
      ...(betType ? [{ key: "bet_type", value: betType }] : []),
      { key: "status", value: betbyStatus(data?.ticketStatus) },
      {
        key: "bet_amount",
        value: money(data?.transaction?.amount ?? transaction.amount),
      },
      betbyPayoutRow(data, request, betslip),
      // Era el único de los seis productos sin la hora de la jugada. Se toma
      // la del BET: el gateway devuelve ese documento aunque el modal se abra
      // desde la fila del premio, igual que el monto apostado y el estado.
      {
        key: "created",
        value: fmtDate(data?.transaction?.createdAt ?? transaction.createdAt),
      },
      ...optionalRow(betbyClosing(data)),
    ],
    selections: bets.map((bet) => ({
      market: bet.market_name,
      selection: bet.outcome_name,
      event: Array.isArray(bet.competitor_name)
        ? bet.competitor_name.join(" vs ")
        : bet.competitor_name,
      league: bet.tournament_name,
      sport: bet.sport_name,
      odds: formatOdds(bet.odds),
      start: fmtUnix(bet.scheduled),
    })),
  };
}

/**
 * CenterHorses devuelve el ticket ya renderizado como HTML (el gateway lo
 * busca por ticketId y lo trae con la sesión del ticket).
 */
function normalizeCenterhorses(data) {
  if (typeof data !== "string" || !data.trim()) return null;
  if (data.trim().toUpperCase() === "NO TICKET") return null;
  return { summary: [], selections: [], html: data };
}

// /report/transaction-details atrapa sus propios errores y responde 200 con un
// sobre { status, From, error } en vez de un status HTTP de error. Hay que
// distinguir "el ticket no está" de "la consulta falló".
const NOT_FOUND_HINTS = [
  "not_found",
  "not found",
  "no se encontro",
  "no se encontró",
];

function unwrapEnvelope(data) {
  if (!data || typeof data !== "object" || data.From !== "getTransactionDetails") {
    return data;
  }

  const message = String(data.error?.message || data.error || "").toLowerCase();
  const isNotFound =
    data.status === 404 ||
    NOT_FOUND_HINTS.some((hint) => message.includes(hint));

  if (isNotFound) return null;
  throw new Error(message || "transaction-details failed");
}

/**
 * El gateway responde siempre envuelto en
 * { response, type, status, connection, isValid } y el payload real del
 * producto vive en `response`. Usamos `connection` como discriminante porque
 * el controlador la setea para todos los productos.
 */
function unwrapGateway(data) {
  if (
    data &&
    typeof data === "object" &&
    !Array.isArray(data) &&
    "response" in data &&
    "connection" in data
  ) {
    return data.response;
  }
  return data;
}

async function resolveUnified({ transaction, whitelabelId }) {
  const { productId } = transaction;

  // BetBy indexa por el id prefijado; el resto por la referencia pelada.
  const transactionId =
    productId === "betby"
      ? prefixedReference(transaction)
      : ticketReference(transaction);

  if (!transactionId) return null;

  const response = await axios.post("/report/transaction-details", {
    productId,
    transactionId,
    whitelabel: whitelabelId,
    currency: transaction.currency,
  });

  const envelope = unwrapEnvelope(response.data);
  if (!envelope) return null;

  const data = unwrapGateway(envelope);
  if (!data) return null;

  switch (productId) {
    case "sportsbook":
      return normalizeSportsbook(data);
    case "sportsw1":
    case "sportsw2":
      return normalizeSw(data);
    case "betby":
      return normalizeBetby(data, transaction);
    case "centerhorses":
      return normalizeCenterhorses(data);
    default:
      return null;
  }
}

const CENTERHORSES_V2_TRANSACTION_ID = /^[A-Z]+-(\d+)$/;
const CENTERHORSES_V2_DESCRIPTION = /#(\d+)/;

function centerhorsesV2TicketId(transaction) {
  const match =
    String(transaction?.transactionID || "").match(CENTERHORSES_V2_TRANSACTION_ID) ||
    String(transaction?.description || "").match(CENTERHORSES_V2_DESCRIPTION);
  return match ? match[1] : "";
}

async function resolveCenterhorsesV2({ transaction, whitelabelId }) {
  const ticketId = centerhorsesV2TicketId(transaction);
  if (!ticketId) return null;

  let data;
  try {
    ({ data } = await axios.post(HORSES_V2_ENDPOINTS.ticketDetail, {
      whitelabelId,
      ticketId,
    }));
  } catch (error) {
    if (error?.response?.status === 404) return null;
    throw error;
  }

  const ticket = normalizeCenterhorses(data?.html);
  if (!ticket) return null;

  return { ...ticket, summary: [{ key: "ticket_number", value: ticketId }] };
}

export async function fetchBetbyClosing({ transactionId, whitelabelId }) {
  const response = await axios.post("/report/transaction-details", {
    productId: "betby",
    transactionId,
    whitelabel: whitelabelId,
  });

  const envelope = unwrapEnvelope(response.data);
  return envelope ? betbyClosing(unwrapGateway(envelope)) : null;
}

/* ------------------------------------------------------------------ */
/* Deportes en vivo - Novubet / NewSport                               */
/* ------------------------------------------------------------------ */
const NOVUBET_CLOSING_PREFIXES = [
  ["CO-", "cashout"],
  ["WIN-", "settlement"],
  ["INS-", "settlement"],
  ["RB-", "refund"],
  ["RO-", null],
];

const novubetClosingKind = (doc) => {
  const id = String(doc?.transactionID || "");
  const match = NOVUBET_CLOSING_PREFIXES.find(([prefix]) => id.startsWith(prefix));
  return match ? match[1] : undefined;
};

export function novubetClosing(transactions) {
  const [last] = (transactions || [])
    .filter((doc) => novubetClosingKind(doc) !== undefined)
    .sort(byCreatedAt)
    .reverse();
  return closingRow(novubetClosingKind(last), last?.createdAt);
}

const amountOrNull = (value) => {
  const parsed = Number(value);
  return value === null || value === undefined || value === "" || !Number.isFinite(parsed)
    ? null
    : parsed;
};

export function novubetSystem(ticket) {
  const systems = Array.isArray(ticket?.TicketSystem) ? ticket.TicketSystem : [];
  const combinations =
    amountOrNull(ticket?.Combinations) ??
    systems.reduce((total, system) => total + (amountOrNull(system?.Combinations) ?? 0), 0);
  if (!combinations || combinations <= 1) return null;

  const systemsStake = systems.reduce(
    (total, system) => total + (amountOrNull(system?.TotalStake) ?? 0),
    0
  );
  const totalStake = systemsStake || amountOrNull(ticket?.Amount);

  return {
    combinations,
    stakePerCombination: totalStake ? totalStake / combinations : null,
  };
}

export function novubetWinRange(ticket) {
  const maxWin = amountOrNull(ticket?.MaxWin);
  const minWin = amountOrNull(ticket?.MinWin);

  return novubetSystem(ticket) && maxWin !== null && minWin !== null && minWin !== maxWin
    ? { min: minWin, max: maxWin }
    : null;
}

const novubetPotentialWin = (ticket) => {
  const range = novubetWinRange(ticket);
  return range
    ? `${money(range.min)} – ${money(range.max)}`
    : money(ticket?.MaxWin ?? ticket?.MinWin);
};

const novubetBetTypeRow = (ticket) => {
  const system = novubetSystem(ticket);
  if (!system) return [];

  return [
    {
      key: "bet_type",
      value: "wallet_ticket.bet_type_system_combinations",
      params: {
        count: system.combinations,
        stake: money(system.stakePerCombination),
      },
    },
  ];
};

async function resolveNovubet({ transaction, whitelabelId }) {
  // El gateway se encarga de quitar el prefijo, así que la referencia pelada
  // sirve igual que el transactionID completo.
  const { data } = await axios.post("/novubet/report/ticket/details", {
    whitelabelId,
    transactionId: ticketReference(transaction),
  });

  const transactions = data?.transactions || [];
  const bet = transactions.find((t) =>
    String(t.transactionID || "").startsWith("BET-")
  );
  const win = transactions.find((t) =>
    String(t.transactionID || "").startsWith("WIN-")
  );
  if (!bet) return null;

  const ticket = bet.details?.request?.Ticket || {};
  const odds = ticket.Odds || [];

  return {
    summary: [
      { key: "ticket_number", value: ticket.Id ?? ticketReference(transaction) },
      ...novubetBetTypeRow(ticket),
      { key: "bet_amount", value: money(bet.amount ?? transaction.amount) },
      {
        key: "potential_win",
        value: win ? money(win.amount) : novubetPotentialWin(ticket),
      },
      { key: "created", value: fmtDate(bet.createdAt) },
      ...optionalRow(novubetClosing(transactions)),
    ],
    selections: odds.map((o) => ({
      market: o?.Market?.Name,
      selection: o?.Odd?.Name,
      event: vs(o?.Match?.Home, o?.Match?.Away),
      league: o?.Tournament?.Name,
      sport: o?.Sport?.Name,
      odds: formatOdds(o?.Odd?.OddValue),
      start: fmtDate(o?.Match?.MatchDate),
    })),
  };
}

/* ------------------------------------------------------------------ */
/* Deportes en vivo - BetCore / BetPrime                               */
/* ------------------------------------------------------------------ */
async function resolveBetprime({ transaction, whitelabelId, userId }) {
  // El endpoint .../ticket/details filtra por transactionID, pero BetCore
  // guarda ese campo vacío: el único identificador útil es referenceId.
  // Por eso vamos por el history filtrado por reference, que ya devuelve los
  // eventos del ticket.
  const { data } = await axios.post("/betprime/report/ticket/history", {
    whitelabelId,
    userId,
    reference: ticketReference(transaction),
    page: 1,
    perPage: 10,
  });

  const items = Array.isArray(data) ? data : data?.data ?? [];
  const ticket = items.find((t) => t.events && t.events.length) || items[0];
  if (!ticket?.events?.length) return null;

  const coef = Number(ticket.coef) || 0;

  return {
    summary: [
      {
        key: "ticket_number",
        value: ticket.referenceId ?? ticketReference(transaction),
      },
      { key: "status", value: ticket.transaction_status },
      { key: "bet_amount", value: money(ticket.amount ?? transaction.amount) },
      {
        key: "potential_win",
        value: money(coef ? Number(ticket.amount) * coef : null),
      },
      { key: "created", value: fmtDate(ticket.createdAt) },
    ],
    selections: (ticket.events || []).map((event) => {
      const odd = (event.odds || [])[0] || {};
      return {
        market: odd["[col_n]"],
        selection: odd["[n]"],
        event: vs(event.opp_1, event.opp_2),
        league: event.league,
        sport: event.sport,
        odds: formatOdds(odd["[c]"]),
        start: fmtUnix(event.start),
      };
    }),
  };
}

/* ------------------------------------------------------------------ */
/* Hipismo por puestos - Puestos Hípicos                               */
/* ------------------------------------------------------------------ */

/**
 * Los estados del proveedor no son los de un ticket deportivo, así que los
 * reducimos a las claves que STATUS_VARIANTS sí conoce para pintar el badge.
 * Las que no aparecen aquí caen en la variante neutra.
 */
const PUESTOS_HIPICOS_STATUS_VARIANTS = {
  proposal: "pending",
  won: "won",
  won_half: "won",
  lost: "lost",
  lost_half: "lost",
};

// Columnas del desglose de la jugada: las mismas que muestra
// Reportes -> Tickets -> Puestos Hípicos, recortadas a lo que cabe en el modal.
const PUESTOS_HIPICOS_SELECTION_FIELDS = [
  { key: "racecourse", labelKey: "wallet_ticket.racecourse" },
  { key: "race", labelKey: "wallet_ticket.race" },
  { key: "horse", labelKey: "puestos_hipicos.labels.horse" },
  { key: "play", labelKey: "puestos_hipicos.labels.play" },
  { key: "amount", labelKey: "labels.amount" },
  { key: "payout", labelKey: "puestos_hipicos.labels.payout" },
  { key: "status", labelKey: "labels.status" },
];

// El API traduce el 404 del gateway a un 400 con este mensaje. No es un fallo
// de la consulta: es que esa jugada no existe.
const PUESTOS_HIPICOS_NOT_FOUND = "TRANSACTION_NOT_FOUND";

const dash = (value) =>
  value === null || value === undefined || value === "" ? "-" : value;

/** "3 - Mi Ejemplar" a partir del número y el nombre del caballo. */
const horseLabel = (bet) =>
  [bet?.horseNumber, bet?.horseName].filter(Boolean).join(" - ");

/**
 * Claves de tipo que el i18n sabe traducir (puestos_hipicos.provider_type.*).
 * Si el gateway empezara a mapear un tipo nuevo sin agregarlo acá y al i18n, el
 * modal muestra el texto crudo del proveedor en vez de una clave sin resolver.
 */
const PUESTOS_HIPICOS_TYPE_KEYS = ["play", "bank"];

/**
 * El proveedor manda el tipo SIN tilde en `type` ("Jugo", "Banqueo"), pero el
 * texto de la jugada que guarda la wallet lo trae CON tilde ("Jugó USA
 * Mountaineer | R4 | ..."). Normalizando acentos las dos fuentes caen en la
 * misma clave.
 */
const PUESTOS_HIPICOS_TYPE_ALIASES = {
  jugo: "play",
  banqueo: "bank",
};

const sinAcentos = (value) =>
  String(value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();

/** play/bank de un ticket del proveedor, tolerando tildes. */
const puestosHipicosTypeKey = (ticket) =>
  (PUESTOS_HIPICOS_TYPE_KEYS.includes(ticket?.typeKey) && ticket.typeKey) ||
  PUESTOS_HIPICOS_TYPE_ALIASES[sinAcentos(ticket?.type)] ||
  null;

/**
 * Tipo de apuesta de la jugada: la jugó el cliente o la banqueó.
 *
 * Manda el ticket del proveedor; el `mode` del dato de la wallet queda de
 * respaldo, porque es la primera palabra del texto de la jugada y sigue estando
 * aunque el proveedor no conteste.
 *
 * El proveedor puede devolver más de un ticket para la misma jugada. Si no
 * coinciden en el tipo, ninguno describe por sí solo la transacción abierta:
 * ahí vale más el dato de la wallet, y si tampoco está no se muestra nada antes
 * que elegir uno de dos tipos contradictorios.
 *
 * Devuelve la clave de i18n cuando se pudo identificar; si no, el texto crudo.
 * El modal lo pasa por $t(), que devuelve la cadena tal cual cuando no es una
 * clave conocida: mismo criterio que providerTypeLabel() en
 * Reportes -> Tickets -> Puestos Hípicos.
 */
const puestosHipicosBetType = (tickets, bet) => {
  const lista = tickets || [];
  const claves = new Set(lista.map(puestosHipicosTypeKey).filter(Boolean));

  const key =
    claves.size === 1
      ? [...claves][0]
      : PUESTOS_HIPICOS_TYPE_ALIASES[sinAcentos(bet?.mode)] || null;

  if (key) return `puestos_hipicos.provider_type.${key}`;
  if (claves.size > 1) return null;

  return lista[0]?.type || bet?.mode || null;
};

/** Estado del proveedor, con la clave de i18n que le corresponde. */
const providerStatus = (row) => {
  if (!row?.statusKey) return row?.status || "";
  return {
    i18nKey: `puestos_hipicos.provider_status.${row.statusKey}`,
    raw: row.status || row.statusKey,
    variantKey: PUESTOS_HIPICOS_STATUS_VARIANTS[row.statusKey] || null,
  };
};

/**
 * Una fila por cada tramo de la jugada. Cuando el proveedor no la desglosa
 * (`rows` vacío) el propio ticket es la única fila.
 */
const puestosHipicosRows = (tickets) =>
  tickets.reduce((rows, ticket) => {
    const parts = ticket?.rows?.length ? ticket.rows : [ticket];

    parts.forEach((part) =>
      rows.push({
        racecourse: dash(part?.bet?.racecourse),
        race: dash(part?.bet?.raceNumber),
        horse: dash(horseLabel(part?.bet)),
        play: dash(part?.bet?.play),
        amount: puestosHipicosAmount(part?.amount),
        payout:
          part?.payout === null || part?.payout === undefined
            ? "-"
            : puestosHipicosAmount(part.payout),
        status: providerStatus(part),
      })
    );

    return rows;
  }, []);

function isPuestosHipicosNotFound(error) {
  const message = error?.response?.data?.message;
  const text = Array.isArray(message) ? message.join(" ") : String(message || "");
  return text.includes(PUESTOS_HIPICOS_NOT_FOUND);
}

async function resolvePuestosHipicos({ transaction, whitelabelId }) {
  // La wallet guarda el número de jugada en `reference` para las tres patas
  // (apuesta, premio y devolución), porque el gateway liquida usando el
  // transaction_id de la apuesta original.
  const referenceId = transaction?.reference ? String(transaction.reference) : "";

  // Si faltara (transacciones antiguas) mandamos el transactionID crudo y deja
  // que el gateway le quite el prefijo. NO sirve ticketReference() acá: asume
  // el formato de los productos deportivos (BET-<ref>) y aquí las liquidaciones
  // son WIN-<ref>-<liq>, LS-<ref>-<liq> y RB-<tipo>-<ref>-<liq>.
  const transactionId = referenceId || String(transaction?.transactionID || "");
  if (!transactionId) return null;

  let data;
  try {
    ({ data } = await axios.post("/puestos-hipicos/report/ticket/details", {
      whitelabelId,
      transactionId,
      ...(referenceId ? { referenceId } : {}),
    }));
  } catch (error) {
    if (isPuestosHipicosNotFound(error)) return null;
    throw error;
  }

  const summary = data?.summary;
  if (!summary) return null;

  const bet = summary.bet || {};
  const betType = puestosHipicosBetType(data?.tickets, bet);

  return {
    summary: [
      { key: "ticket_number", value: summary.referenceId },
      ...(betType ? [{ key: "bet_type", value: betType }] : []),
      { key: "status", value: summary.transaction_status },
      { key: "bet_amount", value: puestosHipicosAmount(summary.betAmount) },
      {
        key: "win_amount",
        labelKey: "puestos_hipicos.labels.win_amount",
        value: puestosHipicosAmount(summary.winAmount),
      },
      // El API ya lo devuelve formateado en el timezone de la whitelabel.
      { key: "created", value: summary.createdAt },
      { key: "racecourse", value: bet.racecourse },
      { key: "race", value: bet.raceNumber },
      {
        key: "horse",
        labelKey: "puestos_hipicos.labels.horse",
        value: horseLabel(bet),
      },
      { key: "play", labelKey: "puestos_hipicos.labels.play", value: bet.play },
    ],
    selections: puestosHipicosRows(data?.tickets || []),
    selectionFields: PUESTOS_HIPICOS_SELECTION_FIELDS,
    // El resumen sale de nuestra propia wallet, así que sigue siendo válido
    // aunque el proveedor no conteste; lo que falta en ese caso es el desglose.
    warningKey: data?.ticketError ? "wallet_ticket.provider_unavailable" : null,
  };
}

const TABLAS_TRANSACTION_ID = /^(?:BET|WIN|RBWIN)-([^-]+)/i;

const TABLAS_MOVEMENT_KEYS = {
  bet: "wallet_ticket.movement_bet",
  win: "wallet_ticket.movement_win",
  loss: "wallet_ticket.movement_loss",
  refund: "wallet_ticket.movement_refund",
};

const TABLAS_SELECTION_FIELDS = [
  { key: "movement", labelKey: "wallet_ticket.movement", translate: true },
  { key: "transactionId", labelKey: "wallet_ticket.transaction_id" },
  { key: "amount", labelKey: "labels.amount" },
  { key: "balance", labelKey: "wallet_ticket.balance" },
  { key: "created", labelKey: "labels.date" },
  { key: "detail", labelKey: "wallet_ticket.provider_detail" },
];

function tablasReference(transaction) {
  if (transaction?.reference) return String(transaction.reference);
  const match = String(transaction?.transactionID || "").match(
    TABLAS_TRANSACTION_ID
  );
  return match ? match[1] : "";
}

const tablasHorse = (bet) => (bet?.horseNumber ? `#${bet.horseNumber}` : null);

async function fetchTablasFamilyTicket(
  endpoint,
  { transaction, whitelabelId, userId }
) {
  const referenceId = tablasReference(transaction);
  const transactionId = String(transaction?.transactionID || referenceId);
  if (!transactionId) return null;

  try {
    const { data } = await axios.post(endpoint, {
      whitelabelId,
      userId,
      transactionId,
      ...(referenceId ? { referenceId } : {}),
    });
    return data?.summary ? data : null;
  } catch (error) {
    if (error?.response?.status === 404) return null;
    throw error;
  }
}

const tablasFamilySummary = (summary) => [
  { key: "ticket_number", value: summary.referenceId },
  { key: "status", value: summary.transaction_status },
  { key: "bet_amount", value: money(summary.betAmount) },
  { key: "win_amount", value: money(summary.winAmount) },
  { key: "created", value: summary.createdAt },
  { key: "settled", value: summary.settledAt },
];

const tablasFamilyMovements = (events) => ({
  selections: (events || []).map((event) => ({
    movement: TABLAS_MOVEMENT_KEYS[event.movement] || event.movement,
    transactionId: dash(event.transactionID),
    amount: money(event.amount),
    balance: money(event.balance),
    created: dash(event.createdAt),
    detail: dash(event.detail),
  })),
  selectionFields: TABLAS_SELECTION_FIELDS,
  selectionsTitleKey: "wallet_ticket.movements",
});

async function resolveTablas(context) {
  const data = await fetchTablasFamilyTicket(
    "/tablas/report/ticket/details",
    context
  );
  if (!data) return null;

  const bet = data.summary.bet || {};

  return {
    summary: [
      ...tablasFamilySummary(data.summary),
      { key: "racecourse", value: bet.racecourse },
      { key: "race", value: bet.raceNumber },
      { key: "horse", value: tablasHorse(bet) },
      { key: "play", value: bet.play },
    ],
    ...tablasFamilyMovements(data.events),
  };
}

async function resolveRojoNegro(context) {
  const data = await fetchTablasFamilyTicket(
    ROJO_NEGRO_ENDPOINTS.details,
    context
  );
  if (!data) return null;

  const bet = data.summary.bet || {};
  const badges = rojoNegroSelectionBadges(bet.selections);

  return {
    summary: [
      ...tablasFamilySummary(data.summary),
      {
        key: "selections",
        labelKey: "rojo_negro.labels.selections",
        value: bet.raw,
        ...(badges.length ? { badges } : {}),
      },
    ],
    ...tablasFamilyMovements(data.events),
  };
}

const DIGITAIN_VARIANT_KEYS = ["pending", "won", "lost"];

const digitainVariantKey = (status) =>
  DIGITAIN_VARIANT_KEYS.includes(status) ? status : null;

const digitainTicketStatus = (status) =>
  status
    ? {
        i18nKey: DIGITAIN_STATUS_LABELS[status] || null,
        raw: status,
        variantKey: digitainVariantKey(status),
      }
    : "";

const digitainSelectionStatusOf = (selection) => {
  const status = digitainSelectionStatus(selection);
  return status
    ? {
        i18nKey: status.i18nKey,
        raw: status.raw,
        variantKey: digitainVariantKey(selection.status),
      }
    : null;
};

function digitainPayoutRow(summary, bet) {
  if (digitainIsSettled(summary.transaction_status)) {
    return { key: "win_amount", value: money(summary.winAmount) };
  }

  const potentialWin = bet.potentialWin;
  return {
    key: "potential_win",
    value:
      potentialWin === null || potentialWin === undefined
        ? null
        : money(potentialWin),
  };
}

async function resolveDigitain({ transaction, whitelabelId, userId }) {
  const referenceId = transaction?.reference ? String(transaction.reference) : "";
  const transactionId = String(transaction?.transactionID || referenceId);
  if (!transactionId) return null;

  let data;
  try {
    ({ data } = await axios.post(DIGITAIN_ENDPOINTS.details, {
      whitelabelId,
      userId,
      transactionId,
      ...(referenceId ? { referenceId } : {}),
    }));
  } catch (error) {
    if (error?.response?.status === 404) return null;
    throw error;
  }

  const summary = data?.summary;
  if (!summary) return null;

  const bet = summary.bet || {};
  const betType = DIGITAIN_BET_TYPE_LABELS[bet.betType] || bet.betTypeName;

  return {
    summary: [
      { key: "ticket_number", value: summary.referenceId },
      ...(betType ? [{ key: "bet_type", value: betType }] : []),
      { key: "status", value: digitainTicketStatus(summary.transaction_status) },
      { key: "bet_amount", value: money(summary.betAmount) },
      { key: "odds", value: bet.odds ? formatOdds(bet.odds) : null },
      digitainPayoutRow(summary, bet),
      { key: "created", value: summary.createdAt },
      { key: "settled", value: summary.settledAt },
    ],
    selections: (bet.selections || []).map((selection) => ({
      market: selection.market,
      selection: selection.selection,
      event: selection.event,
      league: [selection.category, selection.tournament]
        .filter(Boolean)
        .join(" · "),
      sport: selection.sport,
      odds: formatOdds(selection.odds),
      start: selection.eventDate,
      status: digitainSelectionStatusOf(selection),
    })),
  };
}

const RESOLVERS = {
  unified: resolveUnified,
  centerhorsesv2: resolveCenterhorsesV2,
  novubet: resolveNovubet,
  betprime: resolveBetprime,
  puestoshipicos: resolvePuestosHipicos,
  tablas: resolveTablas,
  rojonegro: resolveRojoNegro,
  digitain: resolveDigitain,
};

export function resolverFor(family) {
  return RESOLVERS[family] || null;
}
